import React, {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {assert} from 'assert-ts';
import get from 'lodash/get';
import type {DataSimpleValue} from 'schemaDefinition/types';
import type {ManifestationV4, WorkV4} from 'services/metadata/types';
import {
  selectMetadataEditSavedMetadataSelector,
  useGetManifestationExternalSuggestionsByIdsQuery,
} from 'services/metadata';
import {unique} from 'services/utils';
import {useAppSelector} from 'store/hooks';
import {externalSuggestionsToLinkedLiteraries} from 'scenes/updateMetadata/functions/externalSuggestionsToLinkedLiteraries';
import type {WorkMetadataSuggestions} from './types';

type ExternalSuggestionsContextType = {
  suggest: (valuePath: string) => Array<DataSimpleValue> | undefined;
  getManifestationSuggestions: (
    manifestationId: string,
  ) => Partial<ManifestationV4> | undefined;
  getWorkSuggestions: () => Partial<WorkV4> | undefined;
};

export const ExternalSuggestionsContext = createContext<
  ExternalSuggestionsContextType | undefined
>(undefined);

export const ExternalSuggestionsContextProvider: React.FC<
  PropsWithChildren
> = ({children}) => {
  const metadata = useAppSelector(selectMetadataEditSavedMetadataSelector);

  const manifestationIds = useMemo(() => {
    if (metadata) {
      return metadata.manifestations.map(m => m.id) ?? [];
    }

    return [];
  }, [metadata]);

  const {data} = useGetManifestationExternalSuggestionsByIdsQuery(
    manifestationIds,
    {
      skip: (manifestationIds?.length ?? 0) === 0,
    },
  );

  const suggestions: WorkMetadataSuggestions = useMemo(() => {
    if (data) {
      const series = externalSuggestionsToLinkedLiteraries(data);
      const intellectualLevel = data
        .map(d => d.externalSuggestions.intellectualLevel ?? [])
        .flat();

      return {
        work: {
          intellectualLevel: unique(intellectualLevel),
          links: series,
        },
        expressions: [],
        manifestations: [],
      };
    }

    return {
      work: {},
      expressions: [],
      manifestations: [],
    };
  }, [data]);

  const suggest = useCallback(
    (valuePath: string): Array<DataSimpleValue> | undefined => {
      const suggestion = get(suggestions, valuePath);
      if (suggestion) {
        return Array.isArray(suggestion) ? suggestion : [suggestion];
      }
      return undefined;
    },
    [suggestions],
  );

  const getManifestationSuggestions = useCallback(
    (manifestationId: string) => {
      return suggestions.manifestations.find(m => m.id === manifestationId);
    },
    [suggestions.manifestations],
  );

  const getWorkSuggestions = useCallback(() => {
    return suggestions.work;
  }, [suggestions.work]);

  const value = useMemo(
    () => ({suggest, getManifestationSuggestions, getWorkSuggestions}),
    [suggest, getManifestationSuggestions, getWorkSuggestions],
  );

  return (
    <ExternalSuggestionsContext.Provider value={value}>
      {children}
    </ExternalSuggestionsContext.Provider>
  );
};

export const useExternalSuggestionsContext =
  (): ExternalSuggestionsContextType => {
    return assert(
      useContext(ExternalSuggestionsContext),
      'useExternalSuggestionsContext: context expected',
    );
  };
